const MAX_BUFFER_SIZE = 50
const MAX_EXPORT_REQUESTS = 20
const UNKNOWN_STATUS_TEXT = 'Unknown'
const REDACTED = '[REDACTED]'

const SENSITIVE_URL_PATHS = ['/authenticate', '/refresh-token', '/logout']

const SENSITIVE_HEADER_NAMES = new Set([
  'authorization',
  'refreshtoken',
  'cookie',
  'set-cookie',
  'x-api-key',
  'api-key'
])

const SENSITIVE_BODY_KEYS = new Set([
  'password',
  'token',
  'refreshtoken',
  'refresh_token',
  'access_token',
  'authorization',
  'secret',
  'control',
  'uscontrol'
])

const capturedRequestsBuffer = []

const statusTextByCode = {
  0: 'Pending',
  200: 'OK',
  201: 'Created',
  204: 'No Content',
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable'
}

function normalizeHeaderValue(value) {
  if (Array.isArray(value)) return value.join(', ')
  if (value === undefined || value === null) return ''
  return String(value)
}

function normalizeHeaders(headers = {}) {
  const normalized = {}
  Object.entries(headers).forEach(([name, value]) => {
    normalized[String(name)] = normalizeHeaderValue(value)
  })
  return normalized
}

function toPlainObject(data) {
  if (data === undefined || data === null) return undefined
  if (typeof data === 'string') return data
  if (data instanceof FormData) {
    const formDataObject = {}
    data.forEach((value, key) => {
      formDataObject[key] = typeof value === 'string' ? value : '[binary]'
    })
    return JSON.stringify(formDataObject)
  }
  if (data instanceof URLSearchParams) return data.toString()
  if (data instanceof Blob || data instanceof ArrayBuffer) return '[binary]'

  try {
    return JSON.stringify(data)
  } catch (error) {
    return String(data)
  }
}

function getAbsoluteUrl(url, baseURL) {
  if (!url) return ''
  try {
    const base = baseURL || window.location.origin
    return new URL(url, base).href
  } catch (error) {
    return String(url)
  }
}

function nowTimestamp(duration) {
  return Date.now() - Math.floor(duration || 0)
}

function getUrlPathname(url) {
  try {
    return new URL(url).pathname.toLowerCase()
  } catch (error) {
    return String(url).toLowerCase()
  }
}

function shouldSkipRequestCapture(url) {
  const pathname = getUrlPathname(url)

  if (
    SENSITIVE_URL_PATHS.some(
      (path) => pathname === path || pathname.endsWith(path)
    )
  ) {
    return true
  }

  if (pathname.includes('token')) {
    return true
  }

  if (pathname.includes('changepassword')) {
    return true
  }

  return false
}

function isSensitiveHeader(name) {
  return SENSITIVE_HEADER_NAMES.has(String(name).toLowerCase())
}

function sanitizeHeaders(headers) {
  const sanitized = {}
  Object.entries(headers).forEach(([name, value]) => {
    sanitized[name] = isSensitiveHeader(name) ? REDACTED : value
  })
  return sanitized
}

function sanitizeBodyObject(obj) {
  const sanitized = {}
  Object.entries(obj).forEach(([key, value]) => {
    if (SENSITIVE_BODY_KEYS.has(String(key).toLowerCase())) {
      sanitized[key] = REDACTED
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map((item) => sanitizeBodyValue(item))
    } else if (value !== null && typeof value === 'object') {
      sanitized[key] = sanitizeBodyObject(value)
    } else {
      sanitized[key] = value
    }
  })
  return sanitized
}

function sanitizeBodyValue(value) {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeBodyValue(item))
  }
  if (value !== null && typeof value === 'object') {
    return sanitizeBodyObject(value)
  }
  return value
}

function sanitizeBody(body) {
  if (!body || body === '[binary]') return body

  try {
    const parsed = JSON.parse(body)
    return JSON.stringify(sanitizeBodyValue(parsed))
  } catch (error) {
    return body
  }
}

function sanitizeNetworkEntry(entry) {
  return {
    ...entry,
    requestHeaders: sanitizeHeaders(entry.requestHeaders),
    responseHeaders: sanitizeHeaders(entry.responseHeaders),
    requestBody: sanitizeBody(entry.requestBody),
    responseBody: sanitizeBody(entry.responseBody)
  }
}

function pushCapturedRequest(entry) {
  capturedRequestsBuffer.push(entry)
  if (capturedRequestsBuffer.length > MAX_BUFFER_SIZE) {
    capturedRequestsBuffer.shift()
  }
}

function createRequestKey(request) {
  return `${request.method}:${request.url}`
}

function parseAxiosRequest(config = {}) {
  return {
    method: String(config.method || 'GET').toUpperCase(),
    url: getAbsoluteUrl(config.url, config.baseURL),
    requestHeaders: normalizeHeaders(config.headers || {}),
    requestBody: toPlainObject(config.data)
  }
}

function parseAxiosResponse(response) {
  return {
    responseStatus: Number(response?.status) || 0,
    responseHeaders: normalizeHeaders(response?.headers || {}),
    responseBody: toPlainObject(response?.data)
  }
}

export function startRequestCapture(config = {}) {
  config.__networkCaptureStartTime = performance.now()
  return config
}

function buildCaptureEntry(config, response, duration) {
  const requestData = parseAxiosRequest(config)
  if (shouldSkipRequestCapture(requestData.url)) {
    return
  }

  const responseData = parseAxiosResponse(response)
  pushCapturedRequest(
    sanitizeNetworkEntry({
      ...requestData,
      ...responseData,
      timestamp: nowTimestamp(duration),
      duration: Math.floor(duration)
    })
  )
}

export function completeRequestCapture(response) {
  const config = response?.config || {}
  const duration = Math.max(0, performance.now() - (config.__networkCaptureStartTime || performance.now()))
  buildCaptureEntry(config, response, duration)
  return response
}

export function failRequestCapture(error) {
  const config = error?.config || {}
  const duration = Math.max(0, performance.now() - (config.__networkCaptureStartTime || performance.now()))
  buildCaptureEntry(config, error?.response, duration)
  return error
}

export function captureRecentRequests() {
  const recentItems = capturedRequestsBuffer.slice(-MAX_BUFFER_SIZE)
  const uniqueRequests = new Map()

  recentItems.forEach((request) => {
    const key = createRequestKey(request)
    const existingRequest = uniqueRequests.get(key)
    if (!existingRequest || request.timestamp > existingRequest.timestamp) {
      uniqueRequests.set(key, request)
    }
  })

  return Array.from(uniqueRequests.values())
    .filter((request) => !shouldSkipRequestCapture(request.url))
    .sort((first, second) => second.timestamp - first.timestamp)
    .slice(0, MAX_EXPORT_REQUESTS)
    .map((request) => sanitizeNetworkEntry({ ...request }))
}

export function generateHARFile(requests = []) {
  const nowIso = new Date().toISOString()
  const safeRequests = requests
    .filter((request) => !shouldSkipRequestCapture(request.url))
    .map((request) => sanitizeNetworkEntry({ ...request }))

  const entries = safeRequests.map((request, index) => {
    const requestHeaders = Object.entries(request.requestHeaders || {}).map(([name, value]) => ({
      name: String(name),
      value: String(value)
    }))
    const responseHeaders = Object.entries(request.responseHeaders || {}).map(([name, value]) => ({
      name: String(name),
      value: String(value)
    }))
    const requestBody = request.requestBody
    const responseBody = request.responseBody

    return {
      _connectionId: String(100000 + index),
      _initiator: {
        type: 'script',
        stack: {
          callFrames: []
        }
      },
      _priority: 'High',
      _resourceType: 'xhr',
      cache: {},
      connection: '443',
      pageref: 'page_1',
      request: {
        method: String(request.method || 'GET'),
        url: String(request.url || ''),
        httpVersion: 'HTTP/1.1',
        headers: requestHeaders,
        queryString: [],
        cookies: [],
        headersSize: -1,
        bodySize: requestBody ? String(requestBody).length : 0,
        postData: requestBody
          ? {
              mimeType: request.requestHeaders?.['Content-Type'] || request.requestHeaders?.['content-type'] || 'application/json',
              text: String(requestBody)
            }
          : undefined
      },
      response: {
        status: Number(request.responseStatus) || 0,
        statusText: statusTextByCode[Number(request.responseStatus)] || UNKNOWN_STATUS_TEXT,
        httpVersion: 'HTTP/1.1',
        headers: responseHeaders,
        cookies: [],
        content: responseBody
          ? {
              size: String(responseBody).length,
              mimeType: request.responseHeaders?.['Content-Type'] || request.responseHeaders?.['content-type'] || 'application/json',
              compression: 0,
              text: String(responseBody)
            }
          : {
              size: 0,
              mimeType: request.responseHeaders?.['Content-Type'] || request.responseHeaders?.['content-type'] || 'application/json',
              compression: 0
            },
        redirectURL: '',
        headersSize: -1,
        bodySize: responseBody ? String(responseBody).length : 0,
        _transferSize: -1,
        _error: null,
        _fetchedViaServiceWorker: false
      },
      serverIPAddress: '',
      startedDateTime: new Date(request.timestamp).toISOString(),
      time: Math.max(0, request.duration || 0),
      timings: {
        blocked: -1,
        dns: -1,
        ssl: -1,
        connect: -1,
        send: 0,
        wait: Math.max(0, request.duration || 0),
        receive: 0,
        _blocked_queueing: -1,
        _workerStart: -1,
        _workerReady: -1,
        _workerFetchStart: -1,
        _workerRespondWithSettled: -1
      }
    }
  })

  return JSON.stringify({
    log: {
      version: '1.2',
      creator: {
        name: 'WebInspector',
        version: '537.36'
      },
      pages: [
        {
          startedDateTime: nowIso,
          id: 'page_1',
          title: window.location.href || document.title || 'App',
          pageTimings: {
            onContentLoad: -1,
            onLoad: -1
          }
        }
      ],
      entries
    }
  }, null, 2)
}
